import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import {
  QineticPaymentSource,
  QineticPaymentSourcesService,
} from 'src/app/core/qinetic-payment-sources.service';
import { ERROR_QINETIC_PAYMENT_SOURCES_NOT_FOUND_CODE } from 'src/app/shared/constants/operationals';

@Component({
  selector: 'app-buyers-edit-iban-dialog',
  templateUrl: './buyers-edit-iban-dialog.component.html',
  styleUrls: ['./buyers-edit-iban-dialog.component.scss'],
})
export class BuyersEditIbanDialogComponent implements OnInit {
  buyer_id?: string;
  buyer_qinetic_payment_source_id?: string;
  order_id?: string;
  store_id?: string;

  iban: string = '';
  is_sources_list_running: boolean = false;
  is_save_running: boolean = false;

  payment_source?: QineticPaymentSource;

  constructor(
    private readonly dialog_ref: MatDialogRef<BuyersEditIbanDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private readonly qinetic_payment_sources_service: QineticPaymentSourcesService,
    private readonly error_service: ErrorService,
  ) {
    this.buyer_id = data?.buyer_id;
    this.buyer_qinetic_payment_source_id = data?.buyer_qinetic_payment_source_id;
    this.order_id = data?.order_id;
    this.store_id = data?.store_id;
    this.iban = (data?.iban || '').toUpperCase();
  }

  ngOnInit(): void {
    if (this.buyer_id && (!this.buyer_qinetic_payment_source_id || !this.iban)) {
      this.listPaymentSources(this.buyer_id);
    }
  }

  onCloseClick(): void {
    this.dialog_ref.close();
  }

  onIbanInput(value: string): void {
    this.iban = value.toUpperCase().replaceAll(' ', '');
  }

  canSave(): boolean {
    return !!this.buyer_id && !!this.buyer_qinetic_payment_source_id && !this.isIbanInvalid() && !this.is_save_running;
  }

  onConfirmClick(): void {
    if (!this.canSave()) {
      return;
    }

    const buyer_qinetic_payment_source_id = this.buyer_qinetic_payment_source_id;
    const buyer_id = this.buyer_id;

    if (!buyer_qinetic_payment_source_id || !buyer_id) {
      return;
    }

    this.is_save_running = true;
    this.qinetic_payment_sources_service
      .updatePaymentSource(buyer_qinetic_payment_source_id, buyer_id, {
        iban: this.iban,
        order_id: this.order_id,
        store_id: this.store_id,
      })
      .subscribe({
        next: response => {
          this.dialog_ref.close({ success: true, payment_source: response.data });
          this.is_save_running = false;
        },
        error: error => {
          const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
          this.dialog_ref.close({ error: processed_error });
          this.is_save_running = false;
        },
      });
  }

  listPaymentSources(buyer_id: string): void {
    this.is_sources_list_running = true;
    this.qinetic_payment_sources_service.listPaymentSources(buyer_id).subscribe({
      next: response => {
        const sources = response.data as QineticPaymentSource[];
        this.payment_source = sources?.[0];
        this.buyer_qinetic_payment_source_id = this.payment_source?.buyer_qinetic_payment_source_id;
        this.iban = (this.payment_source?.qinetic_sepa_debit?.iban || '').toUpperCase();
        this.is_sources_list_running = false;
      },
      error: error => {
        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        if (processed_error.code == ERROR_QINETIC_PAYMENT_SOURCES_NOT_FOUND_CODE) {
          this.payment_source = undefined;
          this.buyer_qinetic_payment_source_id = undefined;
          this.iban = '';
        } else {
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
        this.is_sources_list_running = false;
      },
    });
  }

  hasIbanFormatError(): boolean {
    return this.iban.length > 0 && this.isIbanInvalid();
  }

  isIbanInvalid(): boolean {
    const iban = (this.iban || '').trim().toUpperCase();

    if (iban.length !== 27) {
      return true;
    }

    if (!iban.startsWith('IT')) {
      return true;
    }

    if (!/\d/.test(iban[2]) || !/\d/.test(iban[3])) {
      return true;
    }

    if (!/[a-zA-Z]/.test(iban[4])) {
      return true;
    }

    const abi = iban.substring(5, 10);
    if (!/^\d{5}$/.test(abi)) {
      return true;
    }

    const cab = iban.substring(10, 15);
    if (!/^\d{5}$/.test(cab)) {
      return true;
    }

    const last_characters = iban.substring(15, 27);
    if (!/^[A-Za-z0-9]{12}$/.test(last_characters)) {
      return true;
    }

    return false;
  }
}
